import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trash2, Copy, Check, Gift, Calendar, Users, TrendingUp } from 'lucide-react';
import './VoucherManager.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const VoucherManager = () => {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    count: 10,
    debates_limit: 10,
    valid_days: 30,
    max_uses: 1,
    notes: ''
  });

  useEffect(() => {
    fetchVouchers();
  }, []);

  const fetchVouchers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_session_token');
      const response = await axios.get(`${API}/admin/vouchers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setVouchers(response.data.vouchers || []);
    } catch (error) {
      console.error('Error fetching vouchers:', error);
      toast.error('Fehler beim Laden der Vouchers');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (formData.count < 1 || formData.debates_limit < 1 || formData.valid_days < 1) {
      toast.error('Bitte gültige Werte eingeben');
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem('admin_session_token');
      const response = await axios.post(
        `${API}/admin/vouchers/generate`,
        formData,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      toast.success(`${response.data.count} Vouchers erfolgreich generiert!`);
      await fetchVouchers();
      
      // Reset form
      setFormData({
        count: 10,
        debates_limit: 10,
        valid_days: 30,
        max_uses: 1,
        notes: ''
      });
    } catch (error) {
      console.error('Error generating vouchers:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Generieren');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Voucher "${code}" wirklich löschen?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_session_token');
      await axios.delete(`${API}/admin/vouchers/${code}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Voucher gelöscht');
      await fetchVouchers();
    } catch (error) {
      console.error('Error deleting voucher:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Code kopiert!');
    
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const exportVouchers = () => {
    // Export as CSV
    const headers = 'Code,Debates,Gültig (Tage),Max Uses,Verwendet,Status,Notizen\n';
    const rows = vouchers.map(v => 
      `${v.code},${v.debates_limit},${v.valid_days},${v.max_uses},${v.used_count},${v.is_active ? 'Aktiv' : 'Inaktiv'},"${v.notes || ''}"`
    ).join('\n');
    
    const csv = headers + rows;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vouchers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast.success('Vouchers exportiert');
  };

  const getStatusBadge = (voucher) => {
    if (!voucher.is_active) {
      return <Badge variant="destructive">Inaktiv</Badge>;
    }
    if (voucher.used_count >= voucher.max_uses) {
      return <Badge variant="secondary">Voll</Badge>;
    }
    if (voucher.used_count > 0) {
      return <Badge variant="warning">Teilweise verwendet</Badge>;
    }
    return <Badge variant="success">Verfügbar</Badge>;
  };

  const stats = {
    total: vouchers.length,
    active: vouchers.filter(v => v.is_active && v.used_count < v.max_uses).length,
    used: vouchers.filter(v => v.used_count >= v.max_uses).length,
    totalDebates: vouchers.reduce((sum, v) => sum + (v.debates_limit * v.max_uses), 0)
  };

  return (
    <div className="voucher-manager-container">
      <div className="voucher-manager-header">
        <div>
          <h1>Voucher Management</h1>
          <p>Erstelle und verwalte Gutschein-Codes für Beta-Tester und Influencer</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="voucher-stats-grid">
        <Card className="voucher-stat-card">
          <CardContent className="voucher-stat-content">
            <Gift className="voucher-stat-icon" />
            <div>
              <div className="voucher-stat-value">{stats.total}</div>
              <div className="voucher-stat-label">Gesamt Vouchers</div>
            </div>
          </CardContent>
        </Card>

        <Card className="voucher-stat-card">
          <CardContent className="voucher-stat-content">
            <Check className="voucher-stat-icon" style={{color: '#10b981'}} />
            <div>
              <div className="voucher-stat-value">{stats.active}</div>
              <div className="voucher-stat-label">Verfügbar</div>
            </div>
          </CardContent>
        </Card>

        <Card className="voucher-stat-card">
          <CardContent className="voucher-stat-content">
            <Users className="voucher-stat-icon" style={{color: '#f59e0b'}} />
            <div>
              <div className="voucher-stat-value">{stats.used}</div>
              <div className="voucher-stat-label">Verwendet</div>
            </div>
          </CardContent>
        </Card>

        <Card className="voucher-stat-card">
          <CardContent className="voucher-stat-content">
            <TrendingUp className="voucher-stat-icon" style={{color: '#3b82f6'}} />
            <div>
              <div className="voucher-stat-value">{stats.totalDebates}</div>
              <div className="voucher-stat-label">Gesamt Debatten</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Generator Form */}
      <Card className="voucher-generator-card">
        <CardHeader>
          <CardTitle>Neue Vouchers Generieren</CardTitle>
          <CardDescription>Erstelle mehrere Voucher-Codes auf einmal</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="voucher-form-grid">
            <div className="voucher-form-group">
              <Label htmlFor="count">Anzahl Codes</Label>
              <Input
                id="count"
                type="number"
                min="1"
                max="1000"
                value={formData.count}
                onChange={(e) => setFormData({...formData, count: parseInt(e.target.value)})}
              />
            </div>

            <div className="voucher-form-group">
              <Label htmlFor="debates">Debatten pro Code</Label>
              <Input
                id="debates"
                type="number"
                min="1"
                max="9999"
                value={formData.debates_limit}
                onChange={(e) => setFormData({...formData, debates_limit: parseInt(e.target.value)})}
              />
            </div>

            <div className="voucher-form-group">
              <Label htmlFor="days">Gültig (Tage)</Label>
              <Input
                id="days"
                type="number"
                min="1"
                max="365"
                value={formData.valid_days}
                onChange={(e) => setFormData({...formData, valid_days: parseInt(e.target.value)})}
              />
            </div>

            <div className="voucher-form-group">
              <Label htmlFor="maxUses">Max. Einlösungen</Label>
              <Input
                id="maxUses"
                type="number"
                min="1"
                max="1000"
                value={formData.max_uses}
                onChange={(e) => setFormData({...formData, max_uses: parseInt(e.target.value)})}
              />
            </div>

            <div className="voucher-form-group voucher-form-full-width">
              <Label htmlFor="notes">Notizen (optional)</Label>
              <Input
                id="notes"
                type="text"
                placeholder="z.B. 'Für Influencer X'"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
              />
            </div>
          </div>

          <div className="voucher-form-actions">
            <Button 
              onClick={handleGenerate}
              disabled={generating}
              className="voucher-generate-btn"
            >
              {generating ? 'Generiere...' : `${formData.count} Codes Generieren`}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Vouchers List */}
      <Card className="voucher-list-card">
        <CardHeader>
          <div className="voucher-list-header">
            <div>
              <CardTitle>Alle Vouchers ({vouchers.length})</CardTitle>
              <CardDescription>Verwalte und überwache deine Gutschein-Codes</CardDescription>
            </div>
            {vouchers.length > 0 && (
              <Button onClick={exportVouchers} variant="outline">
                CSV Export
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="voucher-loading">Lädt Vouchers...</div>
          ) : vouchers.length === 0 ? (
            <div className="voucher-empty">
              <Gift size={48} />
              <p>Noch keine Vouchers erstellt</p>
            </div>
          ) : (
            <div className="voucher-table-wrapper">
              <table className="voucher-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Debatten</th>
                    <th>Gültig</th>
                    <th>Verwendet</th>
                    <th>Status</th>
                    <th>Notizen</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {vouchers.map((voucher) => (
                    <tr key={voucher.code}>
                      <td>
                        <div className="voucher-code-cell">
                          <code className="voucher-code">{voucher.code}</code>
                          <button
                            className="voucher-copy-btn"
                            onClick={() => copyToClipboard(voucher.code)}
                            title="Code kopieren"
                          >
                            {copiedCode === voucher.code ? (
                              <Check size={16} />
                            ) : (
                              <Copy size={16} />
                            )}
                          </button>
                        </div>
                      </td>
                      <td>{voucher.debates_limit}</td>
                      <td>{voucher.valid_days} Tage</td>
                      <td>{voucher.used_count} / {voucher.max_uses}</td>
                      <td>{getStatusBadge(voucher)}</td>
                      <td className="voucher-notes">{voucher.notes || '-'}</td>
                      <td>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(voucher.code)}
                          className="voucher-delete-btn"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VoucherManager;
